import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Typography, TextField,
  Select, MenuItem, FormControl, InputLabel, Chip,
  IconButton, Switch, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, OutlinedInput, Stack,
  Radio, RadioGroup, FormControlLabel, FormLabel
} from '@mui/material';
import { Delete, Add, Edit } from '@mui/icons-material';
import { budgetsApi, categoriesApi, analyticsApi } from '../services/api';
import { formatMoney, currencySymbol } from '../utils/money';
import { categoryMenuItems, spendingCategories } from '../utils/categoryMenu';

function BudgetSettings() {
  const [budgets, setBudgets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accountIds, setAccountIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [budgetType, setBudgetType] = useState('category'); // 'overall' or 'category'
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    accountIds: [],
    monthlyLimit: '',
    alertThresholds: [70, 90, 100],
    isActive: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [budgetsRes, categoriesRes, analyticsRes] = await Promise.all([
        budgetsApi.getAll(),
        categoriesApi.getAll(),
        analyticsApi.getAccountIds()
      ]);
      
      setBudgets(budgetsRes.data.budgets || []);
      setCategories(categoriesRes.data.categories || []);
      setAccountIds(analyticsRes.data.accountIds || []);
      setError(null);
    } catch (err) {
      console.error('Error loading budget data:', err);
      setError('Failed to load budget data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (budget = null) => {
    if (budget) {
      setEditingBudget(budget);
      setBudgetType(budget.category ? 'category' : 'overall');
      setFormData({
        name: budget.name || budget.category || '',
        category: budget.category || '',
        accountIds: budget.accountIds || [],
        monthlyLimit: budget.monthlyLimit,
        alertThresholds: budget.alertThresholds || [70, 90, 100],
        isActive: budget.isActive
      });
    } else {
      setEditingBudget(null);
      setBudgetType('category');
      setFormData({
        name: '',
        category: '',
        accountIds: [],
        monthlyLimit: '',
        alertThresholds: [70, 90, 100],
        isActive: true
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBudget(null);
    setBudgetType('category');
    setFormData({
      name: '',
      category: '',
      accountIds: [],
      monthlyLimit: '',
      alertThresholds: [70, 90, 100],
      isActive: true
    });
  };

  const handleSave = async () => {
    try {
      if (!formData.name || !formData.monthlyLimit) {
        setError('Please fill in all required fields');
        return;
      }

      if (budgetType === 'category' && !formData.category) {
        setError('Please select a category for category-specific budgets');
        return;
      }

      const dataToSave = {
        ...formData,
        category: budgetType === 'overall' ? null : formData.category
      };

      if (editingBudget) {
        await budgetsApi.update(editingBudget.id, dataToSave);
        setSuccess('Budget updated successfully');
      } else {
        await budgetsApi.create(dataToSave);
        setSuccess('Budget created successfully');
      }
      
      await loadData();
      handleCloseDialog();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving budget:', err);
      setError(err.response?.data?.error || 'Failed to save budget');
    }
  };

  const handleDelete = async (budgetId) => {
    if (!window.confirm('Are you sure you want to delete this budget?')) {
      return;
    }

    try {
      await budgetsApi.delete(budgetId);
      setSuccess('Budget deleted successfully');
      await loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting budget:', err);
      setError('Failed to delete budget');
    }
  };

  const handleToggleActive = async (budget) => {
    try {
      await budgetsApi.update(budget.id, {
        ...budget,
        isActive: !budget.isActive
      });
      await loadData();
    } catch (err) {
      console.error('Error toggling budget:', err);
      setError('Failed to update budget');
    }
  };

  if (loading) {
    return <Typography>Loading budgets...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Budget Management</Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Add Budget
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {budgets.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary" align="center">
              No budgets created yet. Click "Add Budget" to create your first budget.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        budgets.map((budget) => (
          <Card key={budget.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6">
                      {budget.name || budget.category}
                    </Typography>
                    <Chip 
                      label={budget.category || 'Overall'} 
                      color={budget.category ? 'primary' : 'success'}
                      size="small"
                    />
                    <Chip 
                      label={budget.isActive ? 'Active' : 'Inactive'} 
                      color={budget.isActive ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Monthly Limit: {formatMoney(parseFloat(budget.monthlyLimit))}
                  </Typography>
                  
                  {budget.accountIds && budget.accountIds.length > 0 && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Account IDs: {budget.accountIds.join(', ')}
                    </Typography>
                  )}
                  
                  <Typography variant="caption" color="text.secondary">
                    Alert Thresholds: {budget.alertThresholds.join('%, ')}%
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Switch
                    checked={budget.isActive}
                    onChange={() => handleToggleActive(budget)}
                    size="small"
                  />
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenDialog(budget)}
                    color="primary"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDelete(budget.id)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBudget ? 'Edit Budget' : 'Add Budget'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Budget Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Monthly Household Budget, Food Budget"
            />

            <FormControl component="fieldset">
              <FormLabel component="legend">Budget Type</FormLabel>
              <RadioGroup
                row
                value={budgetType}
                onChange={(e) => {
                  setBudgetType(e.target.value);
                  if (e.target.value === 'overall') {
                    setFormData({ ...formData, category: '' });
                  }
                }}
              >
                <FormControlLabel 
                  value="overall" 
                  control={<Radio />} 
                  label="Overall Budget (all categories)" 
                />
                <FormControlLabel 
                  value="category" 
                  control={<Radio />} 
                  label="Category-Specific Budget" 
                />
              </RadioGroup>
            </FormControl>

            {budgetType === 'category' && (
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {/* Budgets are for spending: transfers and investments don't use one up */}
                  {categoryMenuItems(spendingCategories(categories))}
                </Select>
              </FormControl>
            )}

            <TextField
              label="Monthly Limit"
              type="number"
              fullWidth
              required
              value={formData.monthlyLimit}
              onChange={(e) => setFormData({ ...formData, monthlyLimit: e.target.value })}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>{currencySymbol()}</Typography>,
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Account IDs (optional)</InputLabel>
              <Select
                multiple
                value={formData.accountIds}
                onChange={(e) => setFormData({ ...formData, accountIds: e.target.value })}
                input={<OutlinedInput label="Account IDs (optional)" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {accountIds.map((accountId) => (
                  <MenuItem key={accountId} value={accountId}>
                    {accountId}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Leave empty to apply budget to all accounts
              </Typography>
            </FormControl>

            <Box>
              <Typography variant="body2" gutterBottom>
                Alert Thresholds (%)
              </Typography>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Warning"
                  type="number"
                  size="small"
                  value={formData.alertThresholds[0] || 70}
                  onChange={(e) => setFormData({
                    ...formData,
                    alertThresholds: [parseInt(e.target.value), formData.alertThresholds[1], formData.alertThresholds[2]]
                  })}
                  inputProps={{ min: 1, max: 100 }}
                />
                <TextField
                  label="Critical"
                  type="number"
                  size="small"
                  value={formData.alertThresholds[1] || 90}
                  onChange={(e) => setFormData({
                    ...formData,
                    alertThresholds: [formData.alertThresholds[0], parseInt(e.target.value), formData.alertThresholds[2]]
                  })}
                  inputProps={{ min: 1, max: 100 }}
                />
                <TextField
                  label="Exceeded"
                  type="number"
                  size="small"
                  value={formData.alertThresholds[2] || 100}
                  onChange={(e) => setFormData({
                    ...formData,
                    alertThresholds: [formData.alertThresholds[0], formData.alertThresholds[1], parseInt(e.target.value)]
                  })}
                  inputProps={{ min: 1, max: 100 }}
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingBudget ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BudgetSettings;

